import { ArrowRight, Lock } from 'lucide-react';

import { cn } from '../../lib/utils';
import { barWidth, type PreviewScenario } from './scenarios';

export type ScenarioPreviewProps = {
  scenario: PreviewScenario;
  /**
   * Drops the revenue green for a white-label tenant.
   *
   * A Nexoya-branded sales surface in front of an agency's own client is the failure this
   * whole layer exists to prevent, and green is the loudest part of the pitch. The values,
   * the bars and the geometry are identical either way — only the fill changes, because the
   * card is still explaining a capability, just not selling one.
   */
  muted?: boolean;
  className?: string;
};

/**
 * The row geometry, declared once.
 *
 * The header and the rows read from the same constant so the columns cannot drift apart —
 * two grid definitions that started identical and were edited separately is how a header
 * ends up labelling the wrong column.
 */
const ROW_GRID = 'grid grid-cols-[1.15fr_1fr_16px_1fr] items-center gap-x-3';

const COLUMN_HEADING = 'text-[10px] uppercase tracking-[0.6px] text-neutral-400';

/**
 * The track behind a fill. Never carries meaning — the value beside it is the meaning.
 *
 * `neutral-800`, not `neutral-700`, and the reason is the FILL rather than the track: a
 * `neutral-400` "before" bar on a `neutral-700` track measures 2.99:1 and misses the 3:1 WCAG
 * 1.4.11 asks of a meaningful graphic, so the before/after pair the whole card is built to
 * show collapsed into a single visible green bar for a low-vision reader. On `neutral-800`
 * the same fill measures 4.29:1, `green-400` 6.36:1 and the muted `neutral-300` 5.97:1. The
 * track itself drops to 1.28:1 against the card, which is exactly where the card's own
 * dividers already sit — a track carries no meaning and a fill does.
 */
const BAR_TRACK = 'h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-neutral-800';

/** The "before" fill. Grey in both tones — only the recommendation is ever the sales colour. */
const CURRENT_FILL = 'block h-full rounded-full bg-neutral-400';

/**
 * Every `muted` branch is a COMPLETE literal class string, never a base plus an interpolated
 * colour. Tailwind's content scanner is a regex over the file text: `bg-${tone}-400` matches
 * nothing, emits no CSS, and the bar renders as an invisible box with no error anywhere.
 */
const RECOMMENDED_FILL = {
  on: 'block h-full rounded-full bg-green-400',
  off: 'block h-full rounded-full bg-neutral-300',
} as const;

/**
 * The dark preview card: one renderer for all five scenarios.
 *
 * Every scenario is the same `ScenarioRow` shape — a label, two bars, two value texts and an
 * arrow — so there is one component and one set of rendering tests. The channel scenario
 * expresses counts ("3 of 6") rather than percentages, which is the entire reason a row
 * carries a `fraction` for the bar AND a `text` for the reader instead of a single number.
 *
 * SURFACE. `bg-neutral-900` (#151519) is the darkest house token and the only one where the
 * greys below clear 4.5:1. Measured on it: neutral-50 17.41 · neutral-100 14.33 ·
 * neutral-200 10.94 · neutral-300 7.65 · neutral-400 5.49 · green-400 8.15. On `neutral-800`
 * (#2A2A32) neutral-400 drops to 4.29 and fails, which is why the card does not sit there.
 * `neutral-500` measures 3.80 and can never pass on any dark background — its own luminance
 * caps it at 4.38 against pure black — so it appears ONLY on the decorative arrow, and
 * neutral-700/800 only on tracks and dividers, which carry no text.
 *
 * WHY A LIST AND NOT A TABLE. The column headings make a `<table>` look like the honest
 * element, and it was the first thing tried. Two reasons it is not:
 *   1. Header association is a table-navigation-mode feature. In the linear browse mode
 *      people actually read a dialog in, NVDA and VoiceOver announce the cell and not its
 *      column — so a `<th>` would deliver nothing here, while the visually-hidden prefix on
 *      every value delivers "Google Ads, Current: 40%, Recommended: 48%" in every mode.
 *   2. A `<table>` advertises itself as data worth navigating cell by cell. This is sample
 *      data on a sales card. Inviting an analyst to interrogate fabricated figures with
 *      table commands is precisely the wrong promise, and the "Sample data" chip below is
 *      the counterweight the whole card is built around.
 * The trade is real — the prefixes cost repetition a table would not — and it is made
 * knowingly in favour of the mode people will actually be in.
 *
 * The visible header row is therefore `aria-hidden`: the prefixes already say those words
 * once per value, and leaving the header in the tree would say them twice.
 */
export function ScenarioPreview({ scenario, muted = false, className }: ScenarioPreviewProps) {
  const tone = muted ? 'off' : 'on';

  return (
    <section
      data-testid="upsell-scenario"
      aria-label={scenario.title}
      className={cn('rounded-[10px] bg-neutral-900 px-6 py-[22px]', className)}
    >
      <p className="text-[14px] font-medium text-neutral-50">{scenario.title}</p>
      <p className="mt-1 text-[12px] text-neutral-400">{scenario.subtitle}</p>

      <div aria-hidden="true" className={cn(ROW_GRID, 'mt-5 border-b border-neutral-800 pb-2')}>
        <span className={COLUMN_HEADING}>{scenario.columns.subject}</span>
        <span className={COLUMN_HEADING}>{scenario.columns.current}</span>
        <span />
        <span className={COLUMN_HEADING}>{scenario.columns.recommended}</span>
      </div>

      <ul className="mt-1">
        {scenario.rows.map((row) => (
          <li key={row.label} className={cn(ROW_GRID, 'py-2')}>
            {/*
             * WRAPS, never truncates. The label is the row's only identifier, and a clipped
             * one is unrecoverable — there is no tooltip, no `title` and no reveal. `truncate`
             * cut "Native, retail & programmatic" at the design's own 1194px width on the card
             * shown for `channel.unrestricted`, and cut every label at the 320px reflow target
             * of WCAG 1.4.10. A taller row is the whole cost. `break-words` covers the case
             * wrapping alone does not: one word longer than its own column.
             */}
            <span className="min-w-0 break-words text-[13px] text-neutral-200">{row.label}</span>

            <span className="flex min-w-0 items-center gap-2">
              <span className={BAR_TRACK}>
                {/*
                 * INLINE STYLE, DELIBERATELY. The width comes from data, and a data-driven
                 * width cannot be a Tailwind class: the content scanner is a regex over this
                 * file's text, so `w-[${barWidth(...)}]` is a string it never sees, produces
                 * no CSS, and collapses every bar to nothing without a single warning. Do not
                 * "tidy" this into a class.
                 */}
                <span className={CURRENT_FILL} style={{ width: barWidth(row.current.fraction) }} />
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-neutral-300">
                {/* The arrow below is decorative, so the relationship has to arrive as words. */}
                <span className="sr-only">{scenario.columns.current}: </span>
                {row.current.text}
              </span>
            </span>

            <ArrowRight className="h-3.5 w-3.5 text-neutral-500" aria-hidden="true" />

            <span className="flex min-w-0 items-center gap-2">
              <span className={BAR_TRACK}>
                <span className={RECOMMENDED_FILL[tone]} style={{ width: barWidth(row.recommended.fraction) }} />
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-neutral-100">
                <span className="sr-only">{scenario.columns.recommended}: </span>
                {row.recommended.text}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/* No action pill. It illustrated a control the reader cannot reach, in the same green as
          the dialog's real CTA, and a second green button on the surface splits the one decision
          this dialog asks for. The footer says what the capability does; the CTA is the only
          thing that looks clickable. */}
      <div className="mt-3 border-t border-neutral-800 pt-4">
        <p className="text-[13px] font-medium text-neutral-50">{scenario.footerHeadline}</p>
        <p className="mt-1 text-[12px] leading-[1.45] text-neutral-400">{scenario.footerDetail}</p>
      </div>

      {/*
       * The honesty label, and the load-bearing element of this card.
       *
       * Every figure above is fabricated on purpose. This chip is the only thing standing
       * between "illustration" and "claim about your account", so unlike the header row it is
       * NOT `aria-hidden` — a reader who cannot see the numbers still has to be told what
       * they are.
       *
       * The translucent fill carries a `prefers-reduced-transparency` fallback, matching
       * `UpsellTeaser.tsx` — and the fallback repaints the TEXT as well as the background.
       * `bg-white/5` over the card is an effective #1F1F23, where `text-neutral-400` measures
       * 4.96:1; the opaque `neutral-800` it falls back to drops the same text to 4.29:1, under
       * the 4.5:1 that 12px needs, and it is the exact number the ledger above names as the
       * reason the card does not sit on neutral-800. An accessibility accommodation that
       * degrades the one string holding these numerals apart from a claim is worse than none.
       * `neutral-300` measures 5.97:1 there.
       *
       * There is deliberately no `motion-reduce:` opt-out anywhere in this card: a static
       * surface is not motion, and `previewChrome.ts` documents what treating it as motion
       * cost last time.
       */}
      <div className="mt-4 flex justify-center">
        <span
          data-testid="upsell-sample-data"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-neutral-400 [@media(prefers-reduced-transparency:reduce)]:border-neutral-700 [@media(prefers-reduced-transparency:reduce)]:bg-neutral-800 [@media(prefers-reduced-transparency:reduce)]:text-neutral-300"
        >
          <Lock className="h-3 w-3" aria-hidden="true" />
          Sample data · live on the full platform
        </span>
      </div>
    </section>
  );
}
